import * as CryptoJS from "crypto-js";
import { RsaUtil } from "./RsaUtil";
import { AesUtil } from "./AesUtil";
import { AesRsaEncryptedData } from "../interface/AesRsaEncryptedData";
import { ValidationUtil } from "@qid/core";


export class CryptoUtil {

  public static async generateUserKeys(password: string, recoveryPassphrase: string = null): Promise<{
    key: string, publicKey: string, salt: string, iv: string, recoveryKey: any
  }> {

    const rsaKeyPair = await RsaUtil.generateRSAKeyPair();
    const encryptedPrivateKey = await CryptoUtil.encryptPrivateKey(rsaKeyPair.privateBase64, password);

    let encryptedRecoveryKey = null;

    if (!ValidationUtil.isStringEmpty(recoveryPassphrase)) {
      encryptedRecoveryKey = await CryptoUtil.encryptPrivateKey(rsaKeyPair.privateBase64, recoveryPassphrase);
    }

    return {
      key: encryptedPrivateKey.encryptedPrivateKey,
      publicKey: rsaKeyPair.publicKeyBase64,
      salt: encryptedPrivateKey.salt,
      iv: encryptedPrivateKey.iv,
      recoveryKey: encryptedRecoveryKey
    };
  }


  public static async encryptDataWithRsaAndAes(jsonData: any, publicKeyBase64: string): Promise<AesRsaEncryptedData> {

    const aesKey: CryptoKey = await AesUtil.generateKey();

    const encryptedData = await AesUtil.encryptJsonWithAESKey(aesKey, jsonData);
    const aesToEncrypted = new Uint8Array(await window.crypto.subtle.exportKey("raw", aesKey));
    const rsaEncryptedAesKey = await RsaUtil.encrypt(publicKeyBase64, aesToEncrypted);

    return {
      ivBase64: encryptedData.ivB64,
      dataCipher: encryptedData.encryptedDataB64,
      rsaEncryptedAesKey: rsaEncryptedAesKey
    };

  }


  public static async decryptDataWithRsaAndAes(
    cipherData: string,
    privateKeyBase46: string,
    encryptedAesKey: string,
    aesSaltBase64: string) {
    try {
      const aesKey: CryptoKey = await AesUtil.decryptAesKeyWithRsaPrivateKey(privateKeyBase46, encryptedAesKey);
      return AesUtil.decryptJsonWithAESKey(aesKey, CryptoUtil.base64ToUint8Array(cipherData), CryptoUtil.base64ToUint8Array(aesSaltBase64));
    } catch (e) {
    }

  }


  public static unit8ArrayToBase64(unit8Array) {
    let binaryString = "";
    unit8Array.forEach(byte => {
      binaryString += String.fromCharCode(byte);
    });
    return btoa(binaryString);
  }

  public static base64ToUint8Array(base64String) {
    const binaryString = atob(base64String);
    const length = binaryString.length;
    const uint8Array = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      uint8Array[i] = binaryString.charCodeAt(i);
    }

    return uint8Array;
  }


  public static arrayBufferToBase64(arrayBuffer) {
    const binaryString = String.fromCharCode.apply(null, new Uint8Array(arrayBuffer));
    return btoa(binaryString);
  }

  public static base64ToArrayBuffer(base64) {
    const binaryString = window.atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);

    for (let i = 0; i < length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes.buffer;
  }

  public static async importRawKeyFromArrayBuffer(rawKey) {
    return await window.crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM" }, // Adjust the algorithm name if necessary
      false,
      ["encrypt", "decrypt"]
    );
  }

  public static async generatePBKDF2Hash(password: string, salt: string): Promise<any> {
    if (!password || !salt) {
      return null
    }
    const iterations = 10000;
    const keyLength = 256; // in bits
    const algorithm = 'SHA256'; // The hashing algorithm

    return new Promise<string>((resolve, reject) => {
      const derivedKey = CryptoJS.PBKDF2(password, salt, {
        keySize: keyLength / 8,
        iterations,
        hasher: CryptoJS.algo[algorithm]
      });
      resolve(derivedKey);
    });
  }

  public static encode(data: string): Uint8Array {
    const encoder = new TextEncoder();
    return encoder.encode(data);
  }

  public static decode(data: BufferSource): string {
    const decoder = new TextDecoder();
    return decoder.decode(data);
  }


  public static async encryptPrivateKey(privateKey: string, password: string) {


    const salt = crypto.getRandomValues(new Uint8Array(16)); // Random salt
    const encryptionKey = await CryptoUtil.stringToCryptoKey(password, salt);

    const encryptedData = await AesUtil.encryptJsonWithAESKey(encryptionKey, privateKey);

    return {
      salt: CryptoUtil.unit8ArrayToBase64(salt),
      iv: encryptedData.ivB64,
      encryptedPrivateKey: encryptedData.encryptedDataB64,
    };
  }


  public static async decryptPrivateKey(salt: string, iv: string, encryptedPrivateKey: string, password: string) {
    const cryptoKey = await CryptoUtil.stringToCryptoKey(password, CryptoUtil.base64ToUint8Array(salt)); // Derive encryption key
    return AesUtil.decryptJsonWithAESKey(cryptoKey, CryptoUtil.base64ToUint8Array(encryptedPrivateKey), CryptoUtil.base64ToUint8Array(iv))
  }

  static async stringToCryptoKey(password: string, salt): Promise<CryptoKey> {
    const encoder = new TextEncoder();

    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(password),
      { name: "PBKDF2" },
      false,
      ["deriveKey"]
    );

    return await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt,
        iterations: 100000,
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  }

  public static generateSecureKeyphrase(wordsCount: number = 20): string {
    const words = [
      "alpha", "beta", "gamma", "delta", "epsilon", "zeta", "eta", "theta",
      "iota", "kappa", "lambda", "mu", "nu", "xi", "omicron", "pi", "rho",
      "sigma", "tau", "upsilon", "phi", "chi", "psi", "omega", "flare", "orbit",
      "nebula", "quantum", "stellar", "cosmic", "fusion", "nova", "plasma",
      "pulsar", "galaxy", "meteor", "asteroid", "comet", "void", "eclipse",
      "gravity", "zenith", "horizon", "vortex", "solstice", "lunar", "solar",
      "prism", "spectra", "nebula", "aurora", "tidal", "rift", "whisper",
      "echo", "pulse", "cipher", "crystal", "matrix", "horizon", "void",
      "fractal", "phase", "synergy", "fission", "magnet", "pluto", "stratus",
      "sonar", "drift", "cascade", "glyph", "vertex", "flux", "radiant",
      "celestial", "spectrum", "zen", "momentum", "fusion", "axial"
    ];


    const randomIndexes = new Uint32Array(wordsCount);
    crypto.getRandomValues(randomIndexes);

    return Array.from(randomIndexes, (rand) =>
      words[rand % words.length]
    ).join(" ");
  }


}
